// Configuration for data source
declare global {
  interface Window {
    __RUNTIME_CONFIG__?: {
      REACT_APP_BACKEND_BASE_URL?: string;
      REACT_APP_SHOW_SAVED_SCREENSHOTS?: string;
      REACT_APP_SHOW_STATUS_SNACKBAR?: string;
      REACT_APP_SHOW_STARTUP_EULA_BUTTON?: string;
      REACT_APP_ENABLE_ADMINISTRATOR_ROUTE?: string;
      REACT_APP_CHARTS_DEBUG?: string;
      REACT_APP_PRO_PROD_HOST?: string;
      REACT_APP_PRO_PROD_ALLOWED_USERS?: string;
      REACT_APP_PRO_PROD_REDIRECT_URL?: string;
    };
  }
}

const parseBool = (value: unknown): boolean | undefined => {
  if (value == null) return undefined;
  const raw = String(value).trim().toLowerCase();
  if (!raw) return undefined;
  if (['1', 'true', 'yes', 'y', 'on'].includes(raw)) return true;
  if (['0', 'false', 'no', 'n', 'off'].includes(raw)) return false;
  return undefined;
};

const parseCsv = (value: unknown): string[] => {
  if (value == null) return [];
  return String(value)
    .split(',')
    .map((v) => v.trim())
    .filter((v) => v.length > 0);
};

const normalizeHost = (value: string): string => {
  let raw = String(value || '').trim().toLowerCase();
  if (!raw) return '';

  try {
    if (raw.includes('://')) {
      raw = new URL(raw).hostname;
    }
  } catch {
    // Keep parsing with string fallbacks.
  }

  raw = raw
    .replace(/^[a-z]+:\/\//, '')
    .split('/')[0]
    .split(':')[0]
    .replace(/^www\./, '')
    .replace(/\.+$/, '');

  return raw;
};

const DEFAULT_PRO_PROD_HOST = 'garpvi.co.il';
const DEFAULT_PRO_PROD_ALLOWED_USERS = [
  'admin',
  'shaimuli',
  'alonh',
  'michalh',
  'modelpotfolio',
  'vi_course',
  'micalh',
  'garp_course',
];
const DEFAULT_PRO_PROD_REDIRECT_URL = 'https://garpvi.com';

const runtimeShowSavedScreenshots =
  typeof window !== 'undefined' ? window.__RUNTIME_CONFIG__?.REACT_APP_SHOW_SAVED_SCREENSHOTS : undefined;

const showSavedScreenshots =
  parseBool(runtimeShowSavedScreenshots ?? process.env.REACT_APP_SHOW_SAVED_SCREENSHOTS) ??
  true;

const runtimeShowStatusSnackbar =
  typeof window !== 'undefined' ? window.__RUNTIME_CONFIG__?.REACT_APP_SHOW_STATUS_SNACKBAR : undefined;

const runtimeShowStartupEulaButton =
  typeof window !== 'undefined' ? window.__RUNTIME_CONFIG__?.REACT_APP_SHOW_STARTUP_EULA_BUTTON : undefined;

const runtimeEnableAdministratorRoute =
  typeof window !== 'undefined' ? window.__RUNTIME_CONFIG__?.REACT_APP_ENABLE_ADMINISTRATOR_ROUTE : undefined;

const runtimeProProdHost =
  typeof window !== 'undefined' ? window.__RUNTIME_CONFIG__?.REACT_APP_PRO_PROD_HOST : undefined;

const runtimeProProdAllowedUsers =
  typeof window !== 'undefined' ? window.__RUNTIME_CONFIG__?.REACT_APP_PRO_PROD_ALLOWED_USERS : undefined;

const runtimeProProdRedirectUrl =
  typeof window !== 'undefined' ? window.__RUNTIME_CONFIG__?.REACT_APP_PRO_PROD_REDIRECT_URL : undefined;

// When false (default), the bottom-left snackbar is hidden and notifications are suppressed.
// Toggle at runtime (prod) via public/runtime-config.js without rebuilding.
const showStatusSnackbar =
  parseBool(runtimeShowStatusSnackbar ?? process.env.REACT_APP_SHOW_STATUS_SNACKBAR) ?? false;

// Controls whether the startup-login EULA button is visible.
// Default is false so production can keep it hidden unless explicitly enabled.
const showStartupEulaButton =
  parseBool(runtimeShowStartupEulaButton ?? process.env.REACT_APP_SHOW_STARTUP_EULA_BUTTON) ?? false;

// Controls whether /administrator should render at all.
// Default false: when disabled, /administrator renders an empty screen.
const enableAdministratorRoute =
  parseBool(runtimeEnableAdministratorRoute ?? process.env.REACT_APP_ENABLE_ADMINISTRATOR_ROUTE) ?? false;

const proProdHost = normalizeHost(
  runtimeProProdHost ?? process.env.REACT_APP_PRO_PROD_HOST ?? DEFAULT_PRO_PROD_HOST
);

const proProdAllowedUsers = Array.from(
  new Set(
    parseCsv(runtimeProProdAllowedUsers ?? process.env.REACT_APP_PRO_PROD_ALLOWED_USERS ?? DEFAULT_PRO_PROD_ALLOWED_USERS.join(',')).map((u) => u.toLowerCase())
  )
);

const proProdRedirectUrl = String(
  runtimeProProdRedirectUrl ?? process.env.REACT_APP_PRO_PROD_REDIRECT_URL ?? DEFAULT_PRO_PROD_REDIRECT_URL
)
  .trim()
  .replace(/\/$/, '');

export const config = {
  // API-only mode: never fall back to local JSON or mock data.
  // In production, all FMP/Fiscal calls go through the authenticated backend proxy.
  useRealAPI: true,

  // Console noise control
  // When true (and in development), suppress console.log/info/debug.
  // console.warn/error are still shown.
  console: {
    suppressLogs: true,
  },
  
  // API Configuration
  api: {
    baseUrl: 'https://financialmodelingprep.com/stable',
    // Client must use backend proxy; no API keys in the browser.
    apiKey: '',
    defaultSymbol: 'AAPL'
  },

  // Local backend (Admin/JWT)
  backend: {
    // CRA exposes only REACT_APP_* variables.
    // Example: REACT_APP_BACKEND_BASE_URL=http://localhost:5085
    // In production we typically host the API under the same origin at /api.
    // Defaulting to localhost in production causes "Failed to fetch" in the browser.
    // You can still override via window.__RUNTIME_CONFIG__ or REACT_APP_BACKEND_BASE_URL.
    baseUrl: (
      (typeof window !== 'undefined' ? window.__RUNTIME_CONFIG__?.REACT_APP_BACKEND_BASE_URL : undefined) ??
      process.env.REACT_APP_BACKEND_BASE_URL ??
      (process.env.NODE_ENV === 'development' ? 'http://localhost:5085' : '/api')
    )
      .trim()
      .replace(/\/$/, ''),
  },

  features: {
    // Show "My Saved Screenshot" by default (including production).
    // Override via env/runtime with REACT_APP_SHOW_SAVED_SCREENSHOTS=false.
    showSavedScreenshots,
  },

  ui: {
    showStatusSnackbar,
    showStartupEulaButton,
    enableAdministratorRoute,
  },

  proProdAccess: {
    host: proProdHost,
    allowedUsers: proProdAllowedUsers,
    redirectUrl: proProdRedirectUrl,
    normalizeHost,
  },
};
